import { z } from "zod";

/**
 * LevelDefinition — the single data-driven level format (Master Spec §37).
 * The Workshop editor and the game runtime MUST consume this exact schema.
 * Never fork an incompatible "editor format" vs "runtime format".
 *
 * This is a Phase 00 architecture artifact: the shape is locked here so
 * Phase 02+ gameplay and Phase 13/14 Workshop work against the same
 * contract from day one. Concrete `layout`/`entities` geometry vocabulary
 * will be extended (not restructured) as the 3D prototype (Phase 01)
 * proves out what the renderer actually needs.
 */

export const AlgorithmId = z.enum([
  "linear-search",
  "binary-search",
  "bubble-sort",
  "selection-sort",
  "insertion-sort",
]);
export type AlgorithmId = z.infer<typeof AlgorithmId>;

export const StageId = z.enum(["searching", "sorting"]);
export type StageId = z.infer<typeof StageId>;

export const SortDirection = z.enum(["ascending", "descending"]);
export type SortDirection = z.infer<typeof SortDirection>;

/** World 1-10, Level 1-10 within a world (Master Spec §5). */
export const CampaignCoordinates = z.object({
  stage: StageId,
  world: z.number().int().min(1).max(10),
  levelInWorld: z.number().int().min(1).max(10),
  globalLevelNumber: z.number().int().min(1).max(100),
});

/** Searching (§21): target known, positions hidden until scanned. */
export const SearchingDataset = z.object({
  kind: z.literal("searching"),
  values: z.array(z.number()).min(2),
  target: z.number(),
  isSorted: z.boolean(),
  targetPresent: z.boolean(),
});

/** Sorting (§24): all values visible from the start. */
export const SortingDataset = z.object({
  kind: z.literal("sorting"),
  values: z.array(z.number()).min(2),
  sortDirection: SortDirection,
});

export const LevelDataset = z.discriminatedUnion("kind", [
  SearchingDataset,
  SortingDataset,
]);
export type LevelDataset = z.infer<typeof LevelDataset>;

/**
 * Layout/entities are intentionally minimal placeholders in Phase 00.
 * They exist so LevelDefinition round-trips through Zod end-to-end today;
 * the 3D technical artist / level designer roles extend this vocabulary
 * once the Phase 01 vertical prototype validates the presentation layer,
 * per the Algorithm Engine -> Game Domain State -> Presentation Controller
 * -> Three.js Scene rule (Master Spec §13).
 */
export const LevelEntity = z.object({
  id: z.string(),
  kind: z.string(),
  position: z.tuple([z.number(), z.number(), z.number()]).optional(),
});

export const LevelLayout = z.object({
  boxCount: z.number().int().positive(),
  spacing: z.number().positive().default(1.5),
  cameraPreset: z.string().default("elevated-default"),
});

export const MasteryConditions = z.object({
  maxHintsForMastery: z.number().int().nonnegative().default(0),
  maxMistakesForMastery: z.number().int().nonnegative().default(0),
  targetOperationCount: z.number().int().positive().optional(),
});

export const TutorialRule = z.object({
  id: z.string(),
  triggerOn: z.string(),
  message: z.string(),
});

export const LevelDefinition = z.object({
  id: z.string(),
  version: z.number().int().positive().default(1),
  coordinates: CampaignCoordinates,
  algorithm: AlgorithmId,
  learningObjectives: z.array(z.string()).min(1),
  dataset: LevelDataset,
  layout: LevelLayout,
  entities: z.array(LevelEntity).default([]),
  mechanics: z.array(z.string()).default([]),
  tutorialRules: z.array(TutorialRule).default([]),
  masteryConditions: MasteryConditions,
  validation: z.object({
    requiresSortedInput: z.boolean().default(false),
    notes: z.string().optional(),
  }).default({ requiresSortedInput: false }),
});
export type LevelDefinition = z.infer<typeof LevelDefinition>;

/**
 * Cross-field rules Zod's shape alone can't express: Binary Search needs
 * sorted input, and `targetPresent` must actually agree with whether
 * `target` is really in `values` — checked in BOTH directions.
 *
 * Phase 17 (Master Spec §11, Full QA) fixed a real, previously-shipped
 * gap here, found during a cross-phase audit rather than hidden: this
 * function only ever checked "targetPresent: false but target IS in
 * values" — the `targetPresent: true but target is NOT actually in
 * values` direction was never checked, despite being the exact same
 * kind of inconsistency and arguably the more dangerous one (a level
 * that claims a target is findable when Linear/Binary Search can never
 * actually find it, rather than a level that's merely mislabeled after
 * already being unwinnable-as-a-not-found-level). All 100 authored
 * campaign levels were independently verified against this stricter,
 * symmetric rule by Phase 15's own separate Python content self-check
 * (not this function) before being generated, so this fix changes no
 * existing content's validity — it only closes a hole for content
 * authored after Phase 17, most notably Workshop levels (Phase 13),
 * which this function is Workshop's actual runtime validation gate for
 * and which had never been checked against the missing direction at all.
 */
export function validateLevelDefinition(level: LevelDefinition): string[] {
  const errors: string[] = [];
  if (level.algorithm === "binary-search" && level.dataset.kind === "searching") {
    if (!level.dataset.isSorted) {
      errors.push("binary-search levels require dataset.isSorted === true");
    }
  }
  if (level.dataset.kind === "searching") {
    const targetActuallyPresent = level.dataset.values.includes(level.dataset.target);
    if (!level.dataset.targetPresent && targetActuallyPresent) {
      errors.push("targetPresent is false but target value exists in the dataset");
    }
    if (level.dataset.targetPresent && !targetActuallyPresent) {
      errors.push("targetPresent is true but target value does not exist in the dataset");
    }
  }
  return errors;
}

/** Completed steps of a strategy attempt, in execution order. */
export const CampaignPhaseResult = z.object({
  algorithm: AlgorithmId,
  found: z.number().int().nonnegative().nullable(),
  comparisons: z.number().int().nonnegative(), swaps: z.number().int().nonnegative(),
  shifts: z.number().int().nonnegative(), passes: z.number().int().nonnegative(),
  mistakes: z.number().int().nonnegative(), hints: z.number().int().nonnegative(),
  durationMs: z.number().int().nonnegative(),
});
export type CampaignPhaseResult = z.infer<typeof CampaignPhaseResult>;
